import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { Resolver } from 'react-hook-form'

import type { IrrigationDevice } from '@/lib/api/irrigation-devices-api'
import type { IrrigationRenameFormValues } from '@/lib/schemas/irrigation-schemas'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useRenameIrrigationDevice } from '@/hooks/use-irrigation-devices-query'
import { createIrrigationRenameSchema } from '@/lib/schemas/irrigation-schemas'
import { m } from '@/lib/paraglide/messages'

interface Props {
    device: IrrigationDevice
    open: boolean
    onOpenChange: (open: boolean) => void
}

/**
 * Đổi tên hiển thị của trạm trong ERP (SmartLab giữ nguyên tên gốc). Để trống
 * hoặc gõ lại đúng tên gốc là quay về tên SmartLab, để sau này SmartLab đổi
 * tên thì ERP hiện theo.
 */
export function IrrigationRenameDialog({ device, open, onOpenChange }: Props) {
    const rename = useRenameIrrigationDevice()

    const form = useForm<IrrigationRenameFormValues>({
        resolver: zodResolver(
            createIrrigationRenameSchema(),
        ) as Resolver<IrrigationRenameFormValues>,
        defaultValues: { name: device.name },
    })

    useEffect(() => {
        if (!open) return
        form.reset({ name: device.name })
    }, [open, device.name, form])

    const handleSubmit = form.handleSubmit(async ({ name }) => {
        try {
            await rename.mutateAsync({
                device,
                name: name === device.smartlab_name ? '' : name,
            })
            onOpenChange(false)
        } catch {
            // toast đã xử lý trong hook
        }
    })

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{m.irrigation_rename_title()}</DialogTitle>
                </DialogHeader>
                <p className="font-mono text-xs text-muted-foreground">
                    {device.id}
                </p>
                <FormProvider {...form}>
                    <form
                        noValidate
                        onSubmit={handleSubmit}
                        className="space-y-4"
                    >
                        <Field name="name" label={m.irrigation_rename_field()}>
                            <Input
                                id="name"
                                autoFocus
                                autoComplete="off"
                                placeholder={device.smartlab_name}
                                aria-describedby="irrigation-rename-hint"
                                {...form.register('name')}
                            />
                        </Field>
                        <p
                            id="irrigation-rename-hint"
                            className="text-sm text-muted-foreground"
                        >
                            {m.irrigation_rename_hint({
                                name: device.smartlab_name,
                            })}
                        </p>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                            >
                                {m.action_cancel()}
                            </Button>
                            <Button type="submit" disabled={rename.isPending}>
                                {m.common_save()}
                            </Button>
                        </DialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    )
}
